import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { CellClickedEvent, ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { catchError, debounceTime, EMPTY, finalize } from 'rxjs';
import { AGTableBase } from '../../shared/components/table/ag-table-base';
import { Destination } from '../../shared/models/destination.model';
import { Report, ReportsCollection, ScheduledTaskState, UpdateReportDto } from '../../shared/models/scheduled-task.model';
import { Supplier } from '../../shared/models/supplier.model';
import { LiveNumberTestingService } from '../../shared/services/live-number-testing.service';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import {
  SchedulerReportActionData,
  SchedulerReportRequestParams,
  SchedulerService,
  SchedulerTaskView,
  TaskSettingDetail,
} from '../../shared/services/scheduler.service';
import { TestResultUtils } from '../../shared/services/test-result-utils';
import { UsersService } from '../../shared/services/users.service';
import { ReasonForReportComponent } from './reason-for-report/reason-for-report.component';
import { SchedulerReportsTableActionsComponent } from './scheduler-reports-table-actions/scheduler-reports-table-actions.component';
declare const moment: (input?: unknown) => { format: (pattern: string) => string };

@Component({
  standalone: false,
  selector: 'app-scheduler-reports-table',
  templateUrl: './scheduler-reports-table.component.html',
  styleUrls: ['./scheduler-reports-table.component.scss'],
})
export class SchedulerReportsTableComponent extends AGTableBase implements OnInit, OnDestroy {
  @Output() actions = new EventEmitter();

  @ViewChild('taskSettingsModalTpl', { read: TemplateRef, static: false }) taskSettingsModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('commentModalTpl', { read: TemplateRef, static: false }) commentModalTpl: TemplateRef<HTMLTemplateElement>;

  commentModalDialogRef: DialogRef;
  commentTextFormControl: FormControl = new FormControl();
  requestParams = new SchedulerReportRequestParams();
  storageContextName = 'scheduler-reports-table';
  currentRow: Report;
  private scheduler: ReturnType<typeof setInterval>;
  loading = false;
  isLoadedInitially = false;
  taskSettingsDetails: TaskSettingDetail[] = [];
  currentSection = '';
  currentScheduledTaskView: SchedulerTaskView = 'inactive';

  // ng grid
  columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      minWidth: 80,
      maxWidth: 80,
      hide: true,
      sortable: true,
      comparator: () => 0,
      initialSort: 'desc',
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.id}</div>`;
      },
    },
    {
      headerName: 'Date reported',
      field: 'createdAt',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      minWidth: 90,
      maxWidth: 90,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return this.formatDate(_);
      },
    },
    {
      headerName: 'Test ID',
      field: 'testDto',
      minWidth: 90,
      maxWidth: 100,
      tooltipField: 'testDto',
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line clickable"><span>${_.testDto.id}<span></div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Schedule name',
      field: 'scheduledTask.title',
      tooltipField: 'scheduledTaskTitle',
      colId: 'scheduledTaskTitle',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line clickable"><span>${_.scheduledTaskTitle}</span></div>`;
      },
      onCellClicked: (event: CellClickedEvent) => this.onCellClick(event),
    },
    {
      headerName: 'Reason for report',
      field: 'reportRuleDto',
      minWidth: 180,
      maxWidth: 180,
      tooltipField: 'reportRuleDto',
      cellClass: 'd-flex justify-content-center align-items-center',
      cellRenderer: ReasonForReportComponent,
    },
    {
      headerName: 'Test number',
      field: 'testNumber',
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.testDto.destinationDto.phone}</div>`;
      },
    },
    {
      headerName: 'Country(MCC)/Network(MNC)',
      field: 'destination',
      minWidth: 300,
      width: 350,
      maxWidth: 350,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return this.formatDestination(_.testDto.destinationDto);
      },
    },
    {
      headerName: 'Supplier',
      field: 'supplierDto',
      minWidth: 100,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return this.formatSupplier(_.supplierDto);
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: SchedulerReportsTableActionsComponent,
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    },
  ];

  rowData!: Report[];

  // ng grid

  constructor(
    public notificationService: NotificationService,
    public service: SchedulerService,
    public userService: UsersService,
    public storage: LocalStorageService,
    public lntService: LiveNumberTestingService,
    public router: Router,
    public modal: ModalService,
    private localStorage: LocalStorageService
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.paginationPageSize = this.localStorage.get(`sch_report_table_component_size_${this.userService.authUser.id}`) || 20;
    this.columnChange$.pipe(debounceTime(1000)).subscribe(() => {
      this.localStorage.set(`sch_report_table_state_${this.userService.authUser.id}`, this.gridApi.getColumnState());
    });
    this.scheduler = setInterval(() => {
      this.update(false);
    }, 1000 * 30);
    this.actionSubscription = this.service.reportsAction$.subscribe(action => {
      this.onReportAction(action);
    });
  }

  update(spinner = true) {
    if (spinner) {
      this.loading = true;
    }
    this.requestParams.size = this.paginationPageSize;
    this.requestParams.page = this.currentPageNumber - 1;
    this.requestParams.sort = this.sortState.length === 0 ? ['id,desc'] : this.sortState;

    this.service.reportsAll(this.requestParams).subscribe({
      next: (collection: ReportsCollection) => {
        collection.content.map(this.formatCreatedAt);
        this.rowData = collection.content;
        this.totalRowsCount = collection.totalElements;
        this.loading = false;
      },
      error: error => {
        this.notificationService.error({
          title: 'Scheduler',
          message: 'An error occurred while loading reports',
          serviceName: 'SCH',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  onSortChange() {
    const columnState = this.gridApi.getColumnState();
    this.sortState = columnState.map(c => (c.sort ? `${c.colId},${c.sort}` : null));
    this.update();
    this.isLoadedInitially = true;
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    this.localStorage.set(`sch_report_table_component_size_${this.userService.authUser.id}`, size);
    this.update();
  }

  formatRule(row) {
    return `<span class='two-lines'>${row.formattedReason ? row.formattedReason : SchedulerService.formatReportRule(row.reportRuleDto)}</span>`;
  }

  formatDestination(dest: Destination) {
    return `<div class="two-lines">${TestResultUtils.renderDestination(dest, true)}</div>`;
  }

  formatSupplier(supplier: Supplier) {
    return `<span class = "two-lines">${supplier.title} ${supplier.routeType}</span>`;
  }

  formatDate(_: Report) {
    return `<div class="two-lines"><span>${_.createdAtFormat[1]} <br>${_.createdAtFormat[0]}</span></div>`;
  }

  formatCreatedAt(item: Report) {
    const time = typeof item.createdAt === 'number' ? item.createdAt * 1000 : Date.parse(item.createdAt);
    item.createdAtFormat = moment(time).format('DD/MM/YY HH:mm:ss').split(' ');
    return item;
  }

  onCellClick(event: CellClickedEvent) {
    const columnId = event.column.getId();
    const eventData = event.data as Report;

    if (columnId === 'testDto') {
      this.handleLiveTestingRedirect(eventData.testDto.id);
    } else if (columnId === 'scheduledTaskTitle') {
      this.handleScheduledTask(eventData);
    }
  }

  private handleLiveTestingRedirect(testId: number) {
    this.router
      .navigate(['/live-testing'], {
        queryParams: { ids: testId },
      })
      .then(() => {
        this.notificationService.info(`Redirected to Live number testing with test id: ${testId}`, 'Live number testing');
      });
  }

  private handleScheduledTask(eventData: Report) {
    this.taskSettingsDetails = [];
    this.loading = true;
    this.currentScheduledTaskView = 'inactive';

    this.service.getTaskSettings(eventData.scheduledTaskId).subscribe({
      next: ({ task, template, suppliers }) => {
        this.loading = false;
        this.currentScheduledTaskView = this.getTaskView(task.state);
        this.taskSettingsDetails = this.service.consolidateTaskSettings(task, template, eventData, suppliers);
      },
      error: error => this.handleTaskSettingsError(error),
    });

    const dialogRef = this.modal.alert().dialogClass('modal-dialog large-modal').component(this.taskSettingsModalTpl).open();

    dialogRef.result.then(result => {
      if (result) {
        this.router
          .navigate(['/scheduler/tasks'], {
            queryParams: { ids: eventData.scheduledTaskId, view: this.currentScheduledTaskView },
          })
          .then(() => {
            this.notificationService.info(`Redirected to Scheduler with task id: ${eventData.scheduledTaskId}`, 'Scheduler');
          });
      }
    });
  }

  private handleTaskSettingsError(error) {
    this.loading = false;
    this.notificationService.error({
      title: 'Scheduler',
      message: 'An error occurred while loading task settings',
      serviceName: 'SCH',
      requestMessage: error.statusText,
      requestCode: error.status,
      ts: error.timestamp ?? null,
    });
  }

  onSectionChange(sectionId: string) {
    this.currentSection = sectionId ? sectionId : this.taskSettingsDetails[0].id;
  }

  scrollTo(sectionId: string) {
    document.querySelector('#' + sectionId).scrollIntoView();
    setTimeout(() => {
      // Set timeout necessary because scrollspy will trigger on section change when document scrollBy is triggered...
      this.onSectionChange(sectionId);
    }, 0);
  }

  // ng grid
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    const columnState = this.localStorage.get(`sch_report_table_state_${this.userService.authUser.id}`);
    this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
    if (!this.isLoadedInitially) this.update();
  }

  onReportAction(event: SchedulerReportActionData): void {
    this.currentRow = event.row;

    if (event.name === 'comment') {
      this.commentTextFormControl.setValue(event.row.comment);
      this.commentModalDialogRef = this.modal.alert().size('sm').component(this.commentModalTpl).open();
    }
  }

  public onCommentSave(): void {
    if (!this.currentRow) return;

    const updateDto: UpdateReportDto = {
      comment: this.commentTextFormControl.value,
    };

    this.loading = true;
    this.service
      .updateReport(this.currentRow.id, updateDto)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Scheduler',
            message: 'An error occurred while updating the report comment',
            serviceName: 'SCH',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(() => {
        this.commentModalDialogRef.close();
        this.currentRow = null;
        this.notificationService.success('Update report comment successful', 'Scheduler');
        this.update();
      });
  }

  // ng grid

  ngOnDestroy() {
    if (this.scheduler) {
      clearInterval(this.scheduler);
    }
    this.columnChange$.unsubscribe();
    if (this.actionSubscription) {
      this.actionSubscription.unsubscribe();
    }
  }

  private getTaskView(state: ScheduledTaskState): SchedulerTaskView {
    return [ScheduledTaskState.RUNNING, ScheduledTaskState.SCHEDULED, ScheduledTaskState.PAUSED].includes(state) ? 'active' : 'inactive';
  }
}
